/*
 * (c) Copyright, 2024, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useContext, useEffect, useState } from 'react'
import { Button, FormGroup, Form } from 'react-bootstrap'
import { Formik, Field } from 'formik'
import { useServiceContext } from '~/root'
import { useTranslation } from 'react-i18next'
import { toast } from '~/services/toast'
import { getFileSharedLink, sendSharedLinkEmail } from '~/services/api/files'
import Utils from '~/lib/Utils'
import { sendShareLinkEmail } from '~/services/api/files'
import Modal from '~/components/Modal'
import { Select } from '~/components/Select'
import { ErrorMessageStyled } from '~/components/ErrorMessage'
import Validation from '~/lib/Validation'
import _ from 'underscore'
import { usersApiService } from '~/services/api/users'

interface FileDetails {
  id?: string | number
  name?: string
}

interface VersionInfo {
  versionId?: string | number
  minorVersionId?: string | number
}

interface ShareLinkModalProps {
  fileDetails: FileDetails
  versionInfo?: VersionInfo
  onClose?: () => void
}

interface FilterState {
  sharingWith?: string[]
  fileHref?: string
  [key: string]: any
}

interface UserOption {
  value: string
  label: string
}

const ShareLinkModal: React.FC<ShareLinkModalProps> = ({ fileDetails, versionInfo, onClose }) => {
  const context = useServiceContext()
  const { t: translate } = useTranslation()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [availableUsers, setAvailableUsers] = useState<UserOption[]>([])
  const [filter, setFilter] = useState<FilterState>({})

  useEffect(() => {
    setIsOpen(true)
    loadData()
  }, [])

  const doClose = (): void => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const loadData = (): void => {
    const versionId =
      versionInfo && versionInfo.versionId !== undefined && parseInt(String(versionInfo.versionId)) >= 0
        ? parseInt(String(versionInfo.versionId))
        : -1
    const minorVersionId =
      versionInfo && versionInfo.minorVersionId !== undefined && parseInt(String(versionInfo.minorVersionId)) >= 0
        ? parseInt(String(versionInfo.minorVersionId))
        : -1
    if (fileDetails.id !== undefined && fileDetails.id !== null) {
      const fileIdNum = typeof fileDetails.id === 'number' ? fileDetails.id : parseInt(String(fileDetails.id))
      getFileSharedLink(fileIdNum, versionId, minorVersionId).then(function (data) {
        setFilter({ sharingWith: [], fileHref: (data as any).url })
      })
    } else {
      doClose()
    }

    usersApiService.getAssembledUsersGroups().then(function (data: { users: any[] }) {
      setAvailableUsers(Utils.transformToSelectOptions(data.users, 'id', 'name'))
    })
  }

  const ok = (values: FilterState): Promise<any> | undefined => {
    if (values.sharingWith?.length === 0) {
      return
    }

    const apiCall = sendSharedLinkEmail(
      Array.isArray(values.sharingWith)
        ? values.sharingWith.map((v) => (typeof v === 'number' ? v : parseInt(v, 10)))
        : [],
      translate('SHARE_FILE_EMAIL_SUBJECT'),
      values.fileHref ?? ''
    )

    apiCall.then(
      function (data: any) {
        toast.success(translate('BROWSER_SHARE_FILE_SUCCESS_MSG'))
        doClose()
      },
      function (data: any) {
        toast.error(data?.data?.message)
      }
    )

    return apiCall
  }

  return (
    <>
      <Formik
        validate={(values: FilterState) => {
          const errors: { [key: string]: string } = {}
          values?.sharingWith?.length === 0 ? (errors.sharingWith = Validation.general.mixed.required) : null
          return errors
        }}
        enableReinitialize={true}
        innerRef={(el: any) => {
          context.store.formRef = el
        }}
        initialValues={filter}
        onSubmit={(values: FilterState) => {
          ok(values)
          doClose()
        }}
      >
        {({
          values,
          setFieldValue,
          handleSubmit
        }: {
          values: FilterState
          setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
          handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void
        }) => (
          <div>
            <Modal
              className="theme-sandman"
              title={translate('BROWSER_SHARE_FILE_TITLE')}
              show={isOpen}
              handleClose={doClose}
            >
              <Modal.Body>
                <div className="form-group md-form-group wparag w100p">
                  <Form.Label>{translate('DOCUMENT')}</Form.Label>
                  <p className="whitespaceprewrap">{fileDetails.name}</p>
                </div>
                <div className="form-group md-form-group w100p">
                  <Form.Label for="in-sl">{translate('LINK_TO_DOCUMENT')}</Form.Label>
                  <Field type="text" name="fileHref" className="md-input" id="in-sl" />
                </div>
                <div>
                  <FormGroup
                    className="form-group md-form-group w100p"
                    controlId="8c834a26-6f6d-4038-9073-2ac12c510a67"
                  >
                    <Form.Label>{translate('SHARE_WITH')}</Form.Label>
                    <Select
                      placeholder={translate('SELECT_USER(S)...')}
                      name="sharingWith"
                      value={values.sharingWith}
                      options={availableUsers}
                      onChange={(option: any) => {
                        setFieldValue('sharingWith', option ? _.pluck(option, 'value') : [])
                      }}
                      multi={true}
                      showNewOptionAtTop={true}
                      searchable
                      id="8c834a26-6f6d-4038-9073-2ac12c510a67"
                    />
                    <ErrorMessageStyled name="sharingWith" classesName="fs14" />
                  </FormGroup>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button data-action={'ok'} variant="link" onClick={doClose} id="79a0cf30-283a-4f36-9b08-809a90ee08bf">
                  {translate('CANCEL')}
                </Button>
                <Button
                  data-action={'ok'}
                  className="btn btn-primary "
                  onClick={handleSubmit as any}
                  id="35a86d31-d85c-4330-bdb7-ba3d6ad6535c"
                >
                  {translate('OK')}
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        )}
      </Formik>
    </>
  )
}

export default ShareLinkModal
